/**
 * Sentences for the sentence levels: the last level of each world (10, 20 …
 * 100), docs/WORDS_AND_SENTENCES.md.
 *
 * Everyday sentences, written for the game and reviewed by hand under the same
 * rules as the word lists (CLAUDE.md): nothing violent, scary, rude or branded.
 * Each starts with a capital and ends with a full stop, with letters and
 * single spaces in between: no commas, apostrophes, questions or names, so
 * every sentence can be typed on any keyboard, phones included.
 *
 * Lengths grow with the grade. Each grade spans a wide enough range that a
 * level can always be filled exactly, a whole number of sentences; tests check
 * both the format and the lengths (tests/sentences.test.ts).
 *
 * Pure data: no Phaser.
 */
import type { GradeBand } from '../types';

export const SENTENCES: Readonly<Record<GradeBand, readonly string[]>> = {
  3: [
    'Birds sing in spring.',
    'The bell rang loudly.',
    'Fish can swim fast.',
    'We ran to the park.',
    'My cat likes to nap.',
    'The sun is hot today.',
    'Mom made soup for lunch.',
    'The dog sat on my bed.',
    'We play tag after school.',
    'She has a red kite.',
    'The frog can jump very high.',
    'Dad reads me a book at night.',
    'The bus stops at our street.',
    'My friend gave me a green pen.',
    'We saw a bird in the tree.',
    'The milk is in the cold fridge.',
    'Put your shoes by the door.',
    'The baby likes to clap.',
    'It is time to go home.',
    'We made a big snow fort.',
    'The fish swim in the pond.',
    'Grandma bakes the best pie.',
    'My hat blew off in the wind.',
    'We hop and skip down the hill.',
    'The rain made a big puddle.',
    'The duck walks to the lake.',
    'Our class has a pet fish.',
    'We sing a song every morning.',
    'The big brown dog ran across the yard.',
    'My little sister likes to play with blocks.',
  ],
  4: [
    'The bakery smells like warm bread.',
    'The soccer team practiced after school.',
    'She painted a picture of the ocean.',
    'The clock on the wall ticks loudly.',
    'He tied his shoes before the race.',
    'The class cheered when the bell rang.',
    'We cleaned our room before the party.',
    'Please close the door when you leave.',
    'The library has a new reading corner.',
    'We planted beans in the school garden.',
    'My brother forgot his lunch at home.',
    'A gentle breeze moved the tall grass.',
    'We built a tower out of wooden blocks.',
    'The puppy chased its tail in circles.',
    'After dinner we played a card game.',
    'We packed snacks for the long car ride.',
    'We found a smooth stone by the river.',
    'A rainbow appeared after the rain stopped.',
    'The kitten drank milk from a small bowl.',
    'My grandpa tells funny stories at dinner.',
    'The orange cat slept in a sunny window.',
    'The squirrel hid a nut under the leaves.',
    'We wore warm coats on the snowy day.',
    'The farmer filled the barn with fresh hay.',
    'My aunt sent us a letter from the city.',
    'The bright moon lit up the quiet street.',
    'Our neighbor waters her flowers every morning.',
    'The parade marched slowly down the main street.',
    'The teacher wrote the spelling words on the board.',
  ],
  5: [
    'Everyone clapped when the play was over.',
    'The market sells fresh fruit on weekends.',
    'He carefully carried the tray of drinks.',
    'My family visits the lake every summer.',
    'We watched the sunset from the front porch.',
    'The kite drifted high above the green field.',
    'We discovered an old map in the attic.',
    'The hikers rested beside a sparkling waterfall.',
    'We measured the rainfall with a plastic cup.',
    'The baker decorated the cake with tiny stars.',
    'A flock of geese flew south for the winter.',
    'The coach showed us how to pass the ball.',
    'My cousin is learning to play the violin.',
    'The ice cream truck played a cheerful tune.',
    'The village celebrated with music and dancing.',
    'The wind scattered the leaves across the yard.',
    'The robot followed every instruction we gave it.',
    'Our class voted on a name for the hamster.',
    'The librarian helped me find a mystery book.',
    'Our garden grew bright red tomatoes this year.',
    'The pilot announced that we would land soon.',
    'She keeps a journal of the birds she sees.',
    'The students worked together to finish the puzzle.',
    'The scientist looked closely through the microscope.',
    'We followed the trail to the top of the hill.',
    'The orchestra practiced the song for the concert.',
    'The volunteers cleaned the beach on a cool morning.',
    'Clouds gathered as we hurried home before the rain.',
    'My sister practiced her speech in front of the mirror.',
  ],
  6: [
    'The chef sliced the vegetables quickly and carefully.',
    'The town built a new playground beside the river.',
    'Colorful kites filled the sky at the spring festival.',
    'The puzzle had so many pieces that it took a week.',
    'My grandmother taught me how to knit a warm scarf.',
    'A gentle rain fell on the roof all through the night.',
    'The bakery opens early so people can buy fresh bread.',
    'The inventor kept trying until her machine finally worked.',
    'After the long hike everyone was ready for a picnic.',
    'The ferry carried passengers across the harbor every hour.',
    'He wrote a thank you letter to his favorite author.',
    'The museum guide explained how ancient people made pottery.',
    'The detective in the novel solved the mystery in one night.',
    'Bright lanterns floated across the lake during the festival.',
    'Our team designed a bridge that could hold twenty books.',
    'My friends and I organized a bake sale for the school.',
    'The documentary showed how bees communicate with each other.',
    'The gardener explained why worms are good for the soil.',
    'Our class compared the weather in four different cities.',
    'The violinist bowed as the audience stood and applauded.',
    'The newspaper printed a story about our recycling program.',
    'The marching band rehearsed every afternoon before the big parade.',
    'The astronaut described what it feels like to float in space.',
    'We volunteered at the animal shelter during the winter break.',
    'The students interviewed their parents about their first jobs.',
    'The tour bus stopped so we could photograph the mountains.',
    'We packed sandwiches and water for the trip to the canyon.',
    'The architect sketched plans for a library with a glass roof.',
    'Our science project tested which paper towel absorbs the most water.',
    'We practiced our lines until we knew the whole play by heart.',
  ],
  7: [
    'My older cousin is studying to become a veterinarian.',
    'The marathon runners trained for months before the race.',
    'The pilot checked the weather carefully before every flight.',
    'The lighthouse keeper polished the giant lens every evening.',
    'Our robotics team spent the weekend testing a new design.',
    'The editor asked the reporter to check every fact twice.',
    'The engineer explained how a suspension bridge stays balanced.',
    'Our class created a museum exhibit about local inventors.',
    'The mayor thanked everyone who helped rebuild the playground.',
    'Our neighbor restored an old bicycle and gave it to me.',
    'We collected data on how far paper airplanes could glide.',
    'The composer wrote a symphony inspired by the changing seasons.',
    'The students debated whether the school day should start later.',
    'The author signed copies of her newest novel at the bookstore.',
    'Our team analyzed the results and presented them to the class.',
    'The photographer waited patiently for the perfect moment of light.',
    'The community garden provides fresh vegetables for the local shelter.',
    'We studied maps to plan the shortest route through the city.',
    'The choir practiced the harmony until every voice blended together.',
    'The festival celebrated the harvest with music and delicious food.',
    'The botanist explained how desert plants survive with very little water.',
    'We watched a documentary about the migration of monarch butterflies.',
    'The farmers market was crowded with families on a sunny morning.',
    'The chemist carefully measured each liquid before mixing them together.',
    'The archaeologists carefully brushed dirt away from the ancient pottery.',
    'A sudden breeze carried the scent of pine through the open window.',
    'My little brother asked endless questions during the planetarium show.',
    'The historian described how people traveled before trains and cars existed.',
    'The volunteers planted hundreds of trees along the edge of the highway.',
    'The orchestra tuned their instruments as the audience found their seats.',
  ],
  8: [
    'Astronomers announced the discovery of a planet orbiting a distant star.',
    'The librarian recommended a biography about a famous mountain climber.',
    'Students from three schools worked together to build a community garden.',
    'The city replaced old streetlights with bulbs that use far less energy.',
    'The novelist spent years researching the history behind her latest book.',
    'The town held a meeting to discuss plans for the empty lot downtown.',
    'The committee carefully reviewed every proposal before choosing the winning design.',
    'Engineers designed the new bridge to withstand strong winds and heavy traffic.',
    'The students organized a campaign to reduce plastic waste in the cafeteria.',
    'Our class wrote letters to the city council about improving bicycle lanes.',
    'The documentary explored how different cultures celebrate the arrival of spring.',
    'The volunteers recorded the number of birds visiting the wetland each morning.',
    'The engineers tested the robot in the desert before sending it to space.',
    'The pilot explained how air pressure helps an airplane stay in the sky.',
    'After months of practice the orchestra performed the difficult symphony flawlessly.',
    'Our debate team prepared arguments for and against building a new stadium.',
    'The architect explained how sunlight can heat a building without using electricity.',
    'The historian argued that the invention of the printing press changed the world.',
    'The explorer kept a detailed journal describing every day of the long journey.',
    'The research team spent three summers studying how glaciers move and change.',
    'The students compared the advantages and disadvantages of living in a large city.',
    'Our science fair project measured how temperature affects the growth of bean plants.',
    'The museum curator explained why preserving historical documents requires careful planning.',
    'The coach reminded the team that patience and teamwork matter more than winning.',
    'The journalist interviewed several experts before publishing her article on renewable energy.',
    'Our teacher challenged us to write a short story using only one hundred words.',
    'The marine biologist described how coral reefs provide shelter for thousands of species.',
    'The chef explained that the secret to great soup is patience and fresh ingredients.',
    'The scientist presented evidence that the ancient river once flowed much farther north.',
    'The violinist practiced the same difficult passage until her fingers knew it perfectly.',
  ],
};

/** Every word of a sentence as it is shown and typed: "Birds", "sing", "spring." */
export function sentenceWords(sentence: string): string[] {
  return sentence.split(' ');
}
